import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { fetchData } from "../../api/client";
import { getClientLegalName } from "../../utils/clientName";

const fmtIdName = (d) => `#${d?.id ?? "—"} ${getClientLegalName(d) || d?.nome || "Sem nome"}`;

function normalizeDuplicates(duplicates) {
  return Array.isArray(duplicates) ? duplicates.filter(Boolean) : [];
}

function pickPrimaryId(duplicates, preferredId = null) {
  const list = normalizeDuplicates(duplicates);
  if (!list.length) return null;
  if (preferredId != null && list.some((d) => Number(d.id) === Number(preferredId))) {
    return Number(preferredId);
  }
  return Number(list[0].id);
}

/**
 * DuplicateClientDialog
 * Props:
 * - open: boolean
 * - duplicates: Array<{id, nome, matchedBy?: string[]}>
 * - attemptedPayload: object (opcional, usado como patchCandidate no preview)
 * - secondaryClientId: number|null (quando for update/merge real; no create, fica null)
 * - secondaryLabel: string|null (texto só pra orientar o usuário, ex: "cliente que você estava editando")
 * - onClose: () => void
 * - onChangePhone: () => void
 * - onMerged: ({ primaryId }) => void (após commit com sucesso)
 */
export default function DuplicateClientDialog({
  open,
  duplicates,
  attemptedPayload,
  secondaryClientId = null,
  secondaryLabel = null,
  onClose,
  onChangePhone,
  onMerged,
}) {
  const navigate = useNavigate();
  const list = useMemo(() => normalizeDuplicates(duplicates), [duplicates]);
  const [primaryId, setPrimaryId] = useState(() => pickPrimaryId(list));
  const [step, setStep] = useState("choose"); // choose | merge
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [resolvedFields, setResolvedFields] = useState({});

  useEffect(() => {
    if (!open) return;
    setPrimaryId(pickPrimaryId(list));
    setStep("choose");
    setLoading(false);
    setError(null);
    setPreview(null);
    setResolvedFields({});
  }, [open, list]);

  const primaryRow = useMemo(
    () => list.find((d) => Number(d.id) === Number(primaryId)) || null,
    [list, primaryId]
  );

  const primaryMatched = useMemo(() => {
    const mb = primaryRow?.matchedBy;
    if (!Array.isArray(mb) || !mb.length) return null;
    return mb.join(", ");
  }, [primaryRow]);

  const runPreview = async () => {
    if (!primaryId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchData("/clients/merge/preview", "POST", {
        primaryId,
        secondaryId: secondaryClientId || null,
        patchCandidate: attemptedPayload || {},
      });
      const data = resp?.data || resp;
      setPreview(data);
      // inicializa resolvedFields com recommended quando existir
      const next = {};
      (data?.conflicts || []).forEach((c) => {
        if (c?.field) next[c.field] = c.recommended ?? (Array.isArray(c.options) ? c.options[0] : "");
      });
      setResolvedFields(next);
      setStep("merge");
    } catch (e) {
      setError(e?.message || "Não foi possível preparar a unificação.");
    } finally {
      setLoading(false);
    }
  };

  const commitMerge = async () => {
    if (!primaryId) return;
    setLoading(true);
    setError(null);
    try {
      await fetchData("/clients/merge/commit", "POST", {
        primaryId,
        secondaryId: secondaryClientId || null,
        resolvedFields: resolvedFields || {},
      });
      if (onMerged) onMerged({ primaryId });
    } catch (e) {
      setError(e?.message || "Não foi possível concluir a unificação.");
    } finally {
      setLoading(false);
    }
  };

  const closeDisabled = loading;
  const canMerge = !!primaryId && !loading;

  return (
    <Dialog open={open} onClose={closeDisabled ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Telefone já cadastrado</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {error && <Alert severity="error">{error}</Alert>}

          <Typography variant="body2" color="text.secondary">
            Encontramos um cliente já cadastrado com este telefone neste projeto.
          </Typography>

          {secondaryLabel ? (
            <Typography variant="caption" color="text.secondary">
              Contexto: {secondaryLabel}
            </Typography>
          ) : null}

          <Divider />

          {step === "choose" ? (
            <>
              <FormControl>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                  Cliente encontrado
                </Typography>
                <RadioGroup
                  value={primaryId != null ? String(primaryId) : ""}
                  onChange={(e) => setPrimaryId(Number(e.target.value))}
                >
                  {list.slice(0, 3).map((d) => (
                    <FormControlLabel
                      key={d.id}
                      value={String(d.id)}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            {fmtIdName(d)}
                          </Typography>
                          {Array.isArray(d.matchedBy) && d.matchedBy.length ? (
                            <Typography variant="caption" color="text.secondary">
                              Duplicidade por: {d.matchedBy.join(", ")}
                            </Typography>
                          ) : null}
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              {primaryMatched ? (
                <Typography variant="caption" color="text.secondary">
                  Campos em duplicidade: {primaryMatched}
                </Typography>
              ) : null}

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (onChangePhone) onChangePhone();
                  }}
                  disabled={loading}
                >
                  Alterar telefone
                </Button>

                <Button
                  variant="contained"
                  onClick={runPreview}
                  disabled={!canMerge}
                >
                  {loading ? <CircularProgress size={18} /> : "Unificar cadastros"}
                </Button>

                <Button
                  variant="text"
                  onClick={() => {
                    if (!primaryId) return;
                    navigate(`/clients/${primaryId}`);
                    if (onClose) onClose();
                  }}
                  disabled={!primaryId || loading}
                >
                  Abrir cliente
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <Typography variant="subtitle2">Escolha o que deve permanecer</Typography>
              <Typography variant="body2" color="text.secondary">
                Vamos manter o cliente {fmtIdName(primaryRow)} como principal (ID antigo) e aplicar suas escolhas.
              </Typography>

              {preview?.affectedRefs ? (
                <Typography variant="caption" color="text.secondary">
                  Itens que serão migrados: solicitações ({preview.affectedRefs.leads || 0}), mensagens ({preview.affectedRefs.leadsmensagens || 0}), pesquisas ({preview.affectedRefs.pesquisasrespostas || 0}).
                </Typography>
              ) : null}

              <Divider />

              {Array.isArray(preview?.conflicts) && preview.conflicts.length ? (
                <Stack spacing={1.25}>
                  {preview.conflicts.map((c) => {
                    const field = c.field;
                    const options = Array.isArray(c.options) ? c.options : [];
                    if (!field || options.length === 0) return null;
                    const value = resolvedFields?.[field] ?? "";
                    return (
                      <FormControl key={field} fullWidth size="small">
                        <InputLabel>{field}</InputLabel>
                        <Select
                          label={field}
                          value={value}
                          onChange={(e) =>
                            setResolvedFields((prev) => ({
                              ...(prev || {}),
                              [field]: e.target.value,
                            }))
                          }
                        >
                          {options.map((opt) => (
                            <MenuItem key={`${field}:${String(opt)}`} value={opt}>
                              {String(opt)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  })}
                </Stack>
              ) : (
                <Alert severity="info">
                  Não encontramos conflitos entre os dados. Você pode concluir a unificação.
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        {step === "merge" ? (
          <>
            <Button onClick={() => setStep("choose")} disabled={loading}>
              Voltar
            </Button>
            <Button onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={commitMerge} disabled={!canMerge}>
              {loading ? <CircularProgress size={18} /> : "Concluir unificação"}
            </Button>
          </>
        ) : (
          <Button onClick={onClose} disabled={loading}>
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

